import type { IntervalQuestion } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { type Stab, useStabScheduler } from '../player/stab-scheduler.js'

/**
 * Ear Training Interval ID audition: two sequential single-note stabs
 * separated by GAP_MS. Each note is a quarter-note at 100 BPM (~600 ms);
 * total audition ~1400 ms one-shot. The 200 ms gap matches Function Quiz's
 * stab boundary (Bowie §3.4 / §8.2 legato-staccato edge).
 *
 * Spec: docs/superpowers/specs/2026-05-20-ear-training-suite-design.md §4.4
 */
const NOTE_MS = 600
const GAP_MS = 200

export const buildIntervalStabs = (
  rootNote: NoteName,
  rootOctave: number,
  targetNote: NoteName,
  targetOctave: number
): Stab[] => [
  {
    events: [{ type: 'note' as const, note: `${rootNote}${rootOctave}` as NoteName, duration: '4n' }],
    delayMs: 0,
  },
  {
    events: [{ type: 'note' as const, note: `${targetNote}${targetOctave}` as NoteName, duration: '4n' }],
    delayMs: NOTE_MS + GAP_MS,
  },
]

export interface UseIntervalPlaybackProps {
  question: IntervalQuestion | null
}

/**
 * Hook returning `{ play, stop }`. Thin wrapper over `useStabScheduler`.
 * A null question → empty stab list → no-op play, so the caller can mount
 * the hook unconditionally regardless of the active sub-mode.
 */
export const useIntervalPlayback = (props: UseIntervalPlaybackProps): { play: () => void; stop: () => void } => {
  const { play, stop } = useStabScheduler({
    buildStabs: () =>
      props.question
        ? buildIntervalStabs(
            props.question.rootNote,
            props.question.rootOctave,
            props.question.targetNote,
            props.question.targetOctave
          )
        : [],
  })
  return { play, stop }
}
